import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, MapPin, Clock, Phone, Mail, Store, Navigation, ArrowLeft,
  ShieldCheck, Sparkles, ChevronLeft, ChevronRight, X,
} from "lucide-react";
import { GlassCard } from "../components/common/ui";
import { Button } from "../components/ui/button";
import api from "../lib/api";

function cleanDescription(store) {
  let d = (store.description || "").trim();
  const name = (store.name || "").trim();
  // Presentation fix (#6): drop a leading duplicate of the store name so the text
  // reads naturally (e.g. "Weeley Post Office & Store Located in…" -> "Located in…").
  if (name && d.toLowerCase().startsWith(name.toLowerCase())) {
    d = d.slice(name.length).replace(/^[\s.,–—-]+/, "");
    if (d) d = d.charAt(0).toUpperCase() + d.slice(1);
  }
  return d;
}

function MapSection({ store }) {
  if (!store.map_embed_url && !store.directions_url) return null;
  return (
    <GlassCard className="overflow-hidden" hover={false} data-testid="store-map-section">
      <div className="p-5 flex items-center justify-between gap-3">
        <h2 className="font-playfair text-lg text-white flex items-center gap-2"><MapPin className="h-5 w-5 text-gold" /> Find us</h2>
        {store.directions_url && (
          <a href={store.directions_url} target="_blank" rel="noopener noreferrer" data-testid="store-directions-btn">
            <Button className="cf-gold-btn font-semibold gap-2"><Navigation className="h-4 w-4" /> Get Directions</Button>
          </a>
        )}
      </div>
      {store.map_embed_url && (
        <iframe
          title={`Map — ${store.name}`}
          src={store.map_embed_url}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="w-full h-[340px] sm:h-[420px] border-0"
          data-testid="store-map-iframe"
        />
      )}
    </GlassCard>
  );
}

function Gallery({ images }) {
  const [active, setActive] = useState(null);
  if (!images?.length) return null;
  const items = images.filter((g) => g.image);
  if (!items.length) return null;
  return (
    <div data-testid="store-gallery">
      <h2 className="font-playfair text-2xl text-white">Gallery</h2>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
        {items.map((g, i) => (
          <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
            className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-white/10 bg-black/40">
            <img src={g.image} alt={g.caption || `${"Gallery image"} ${i + 1}`} loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]" />
          </button>
        ))}
      </div>
      {active !== null && (
        <div className="fixed inset-0 z-[100] bg-black/90 grid place-items-center p-4" onClick={() => setActive(null)} data-testid="gallery-lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setActive(null)}><X className="h-7 w-7" /></button>
          <button className="absolute left-4 sm:left-8 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setActive((a) => (a - 1 + items.length) % items.length); }}><ChevronLeft className="h-9 w-9" /></button>
          <img src={items[active].image} alt={items[active].caption || ""} className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-4 sm:right-8 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setActive((a) => (a + 1) % items.length); }}><ChevronRight className="h-9 w-9" /></button>
          {items[active].caption && <p className="absolute bottom-6 text-center w-full text-white/80 text-sm px-4">{items[active].caption}</p>}
        </div>
      )}
    </div>
  );
}

export default function StoreDetail() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/stores/${slug}`)
      .then(({ data }) => setStore(data))
      .catch((err) => { if (err.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  // SEO title/description + per-store LocalBusiness JSON-LD (own script, not the
  // shared SeoHead data-seo scripts, so there is no conflict).
  useEffect(() => {
    if (!store) return;
    document.title = store.seo_title || `${store.name} · Clacton Fireworks`;
    let node = null;
    if (store.schema) {
      node = document.createElement("script");
      node.type = "application/ld+json";
      node.setAttribute("data-store-schema", "1");
      node.textContent = JSON.stringify(store.schema);
      document.head.appendChild(node);
    }
    return () => {
      document.title = "Clacton Fireworks";
      if (node) node.remove();
    };
  }, [store]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  if (notFound || !store) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-32 text-center" data-testid="store-not-found">
        <Store className="h-10 w-10 mx-auto text-gold/50" />
        <h1 className="mt-4 font-playfair text-2xl text-white">Store not found</h1>
        <p className="mt-2 text-muted-foreground">This store may have moved or is no longer available.</p>
        <Link to="/stores" className="mt-6 inline-flex items-center gap-2 text-gold hover:underline"><ArrowLeft className="h-4 w-4" /> Back to all stores</Link>
      </div>
    );
  }

  const addressLines = [store.address_line1, store.address_line2,
    [store.town, store.city_county].filter(Boolean).join(", "), store.postcode].filter(Boolean);
  const enabledServices = (store.services || []).filter((s) => s && s.name);
  const descParagraphs = cleanDescription(store).split(/\n{2,}|\n/).map((p) => p.trim()).filter(Boolean);

  return (
    <div data-testid="store-detail-page">
      {/* Hero */}
      <div className="relative">
        <div className="absolute inset-0 h-[46vh] min-h-[320px] overflow-hidden">
          {store.featured_image
            ? <img src={store.featured_image} alt={store.name} className="h-full w-full object-cover opacity-40" />
            : <div className="h-full w-full bg-gradient-to-b from-gold/10 to-transparent" />}
          <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/70 to-ink" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-16 pb-10">
          <Link to="/stores" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-gold" data-testid="back-to-stores">
            <ArrowLeft className="h-4 w-4" /> All stores
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            {(store.primary_logo || store.secondary_logo) && (
              <div className="flex items-center gap-3" data-testid="store-logos">
                {store.primary_logo && (
                  <img src={store.primary_logo} alt={`${store.name} logo`} data-testid="store-primary-logo"
                    className="h-32 w-32 rounded-xl object-contain bg-white border border-white/10 p-3" />
                )}
                {store.secondary_logo && (
                  <img src={store.secondary_logo} alt={`${store.name} secondary logo`} data-testid="store-secondary-logo"
                    className="h-32 w-32 rounded-xl object-contain bg-white border border-white/10 p-3" />
                )}
              </div>
            )}
            <div>
              {store.is_collection_point && (
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-gold"><Store className="h-3.5 w-3.5" /> Collection Point</span>
              )}
              <h1 className="mt-1 font-cinzel text-2xl sm:text-3xl lg:text-4xl text-white tracking-wide">{store.name}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Description (left) + Available services (right) — stacks on mobile */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-8 pb-2 grid gap-10 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2">
          <h2 className="font-playfair text-xl text-white">About this store</h2>
          {descParagraphs.length > 0 && (
            <div className="mt-4 max-w-[62ch] space-y-4 text-left text-white/80 leading-relaxed" data-testid="store-description">
              {descParagraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          )}
        </div>
        {enabledServices.length > 0 && (
          <div data-testid="store-services-panel">
            <h2 className="font-playfair text-xl text-white">Available Services</h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-3">
              {enabledServices.map((sv, i) => (
                <div key={i} data-testid={`service-panel-${i}`}
                  className="flex flex-col items-center text-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  {sv.image
                    ? <div className="h-14 w-14 grid place-items-center rounded-lg bg-white p-1.5 border border-white/10">
                        <img src={sv.image} alt={`${sv.name} logo`} loading="lazy" className="max-h-full max-w-full object-contain" />
                      </div>
                    : <div className="h-14 w-14 grid place-items-center rounded-lg bg-white/10 border border-white/10"><Store className="h-6 w-6 text-gold" aria-hidden="true" /></div>}
                  <span className="text-xs text-white/85 leading-tight flex items-center gap-1">{sv.featured && <Sparkles className="h-3 w-3 text-gold shrink-0" aria-hidden="true" />}{sv.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-24 grid gap-8 lg:grid-cols-3">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-10">
          <MapSection store={store} />

          <Gallery images={store.gallery} />
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <GlassCard className="p-6" hover={false} data-testid="store-info-card">
            <h2 className="font-playfair text-lg text-white">Store information</h2>
            <div className="mt-4 space-y-4 text-sm">
              {addressLines.length > 0 && (
                <div className="flex items-start gap-3" data-testid="store-address">
                  <MapPin className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                  <div className="text-white/80">{addressLines.map((l, i) => <div key={i}>{l}</div>)}</div>
                </div>
              )}
              {store.opening_hours_display?.length > 0 && (
                <div className="flex items-start gap-3" data-testid="store-hours">
                  <Clock className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                  <div className="text-white/80">{store.opening_hours_display.map((h, i) => <div key={i}>{h}</div>)}</div>
                </div>
              )}
              {store.phone && (
                <a href={`tel:${store.phone}`} className="flex items-center gap-3 text-white/80 hover:text-gold" data-testid="store-phone"><Phone className="h-4 w-4 text-gold shrink-0" /> {store.phone}</a>
              )}
              {store.email && (
                <a href={`mailto:${store.email}`} className="flex items-center gap-3 text-white/80 hover:text-gold break-all" data-testid="store-email"><Mail className="h-4 w-4 text-gold shrink-0" /> {store.email}</a>
              )}
            </div>
            {store.directions_url && (
              <a href={store.directions_url} target="_blank" rel="noopener noreferrer" className="block mt-5">
                <Button className="w-full cf-gold-btn font-semibold gap-2" data-testid="sidebar-directions-btn"><Navigation className="h-4 w-4" /> Get Directions</Button>
              </a>
            )}
          </GlassCard>

          <GlassCard className="p-6" hover={false}>
            <h3 className="font-playfair text-lg text-white">Reserve & collect here</h3>
            <p className="mt-2 text-sm text-white/70">Browse our range, reserve online and pay in store when you collect.</p>
            <Link to="/fireworks" className="block mt-4"><Button className="w-full cf-gold-btn font-semibold gap-2" data-testid="store-shop-btn"><Sparkles className="h-4 w-4" /> Browse Fireworks</Button></Link>
            <p className="mt-4 flex items-start gap-2 text-xs text-white/50"><ShieldCheck className="h-4 w-4 text-gold shrink-0" /> 18+ only · Challenge 25 applies. Bring valid photo ID.</p>
          </GlassCard>
        </aside>
      </div>
    </div>
  );
}
